/**
 * FERP Portal - Icon Map Utility
 *
 * Maps icon string names (from SubgraphMetadata) to Tabler icon components.
 * Used for data-driven navigation where icon names come from backend.
 *
 * FERP Principles:
 * - Data-driven: Icons defined in backend, rendered in frontend
 * - Extensible: Add new icon mappings as subgraphs are added
 */

import type { Icon } from '@tabler/icons-react';
import {
  // Core navigation icons
  IconDashboard,
  IconInbox,
  IconChecklist,
  IconSettings,
  IconUsers,
  IconBuildingFactory2,
  IconNetwork,
  IconTemplate,
  IconTopologyStar3,

  // Monitor icons
  IconChartBar,
  IconAlertTriangle,
  IconFileText,

  // Govern icons
  IconShieldCheck,
  IconAlertOctagon,
  IconFileSearch,

  // Execute icons
  IconGitBranch,
  IconCalendarEvent,
  IconArrowsShuffle,
  IconSitemap,

  // Group section icons (collapsible nav groups)
  IconClipboardList,
  IconTool,
  IconActivity,
  IconShieldLock,
  IconCode,
  IconPlayerPlay,

  // Nav item icons (referenced by generateCoreNavItems)
  IconForms,
  IconUser,
  IconRubberStamp,
  IconDatabase,
  IconClock,
  IconBuilding,
  IconScale,
  IconBuildingFactory,
  IconReceipt,
  IconMinus,
  IconFileInvoice,
  IconBook,
  IconUserSearch,

  // Subgraph-specific icons
  IconHeartbeat,
  IconCash,
  IconCertificate,
  IconWorld,
  IconCalculator,
  IconStack,
  IconStethoscope,
  IconReportMoney,
  IconCurrencyDollar,

  // Backend seed data icons (seed_subgraph_data.rs)
  IconHeartRateMonitor,
  IconPill,
  IconListTree,
  IconBuildingStore,
  IconUserDollar,
  IconId,
  IconHierarchy,
  IconTable,
  IconNotes,

  // Module browser icons
  IconLayoutDashboard,
  IconPuzzle,
  IconComponents,

  // FHIR entity type icons (smart navigation curated defaults)
  IconTestPipe,

  // Task Templates / LLM
  IconSparkles,

  // Document Studio (Chaos-to-DSL pipeline)
  IconFileAnalytics,

  // New nav items (constellation, system, AI tools)
  IconStars,
  IconLayoutGrid,
  IconListDetails,
  IconSettings2,
  IconUserCircle,
  IconShield,
  IconBuildingSkyscraper,
  IconMapPin,
  IconTimelineEvent,
  IconCircleDot,
  IconLayoutColumns,

  // Fallback
  IconBox,
} from '@tabler/icons-react';

/**
 * Map of icon names to Tabler icon components.
 *
 * Icon names match those stored in SubgraphMetadata.nav_items[].icon
 */
export const ICON_MAP: Record<string, Icon> = {
  // Core navigation
  IconDashboard,
  IconInbox,
  IconChecklist,
  IconSettings,
  IconUsers,
  IconBuildingFactory2,
  IconNetwork,
  IconTemplate,
  IconTopologyStar3,

  // Monitor
  IconChartBar,
  IconAlertTriangle,
  IconFileText,

  // Govern
  IconShieldCheck,
  IconAlertOctagon,
  IconFileSearch,

  // Execute
  IconGitBranch,
  IconCalendarEvent,
  IconArrowsShuffle,
  IconSitemap,

  // Group section icons (collapsible nav groups)
  IconClipboardList,
  IconTool,
  IconActivity,
  IconShieldLock,
  IconCode,
  IconPlayerPlay,

  // Nav item icons (referenced by generateCoreNavItems)
  IconForms,
  IconUser,
  IconStamp: IconRubberStamp,
  IconDatabase,
  IconClock,
  IconBuilding,
  IconScale,
  IconBuildingFactory,
  IconReceipt,
  IconMinus,
  IconFileInvoice,
  IconBook,
  IconUserSearch,

  // Subgraph-specific
  IconHeartbeat,
  IconCash,
  IconCertificate,
  IconWorld,
  IconCalculator,
  IconStack,
  IconStethoscope,
  IconReportMoney,
  IconCurrencyDollar,

  // Backend seed data icons (seed_subgraph_data.rs)
  IconHeartRateMonitor,
  IconPill,
  IconListTree,
  IconBuildingStore,
  IconUserDollar,
  IconId,
  IconHierarchy,
  IconTable,
  IconNotes,

  // Module browser
  IconLayoutDashboard,
  IconPuzzle,
  IconComponents,

  // FHIR entity type icons
  IconTestPipe,

  // Task Templates / LLM
  IconSparkles,

  // Document Studio (Chaos-to-DSL pipeline)
  IconFileAnalytics,

  // New nav items (constellation, system, AI tools)
  IconStars,
  IconLayoutGrid,
  IconListDetails,
  IconSettings2,
  IconUserCircle,
  IconShield,
  IconBuildingSkyscraper,
  IconMapPin,
  IconTimelineEvent,
  IconCircleDot,
  IconLayoutColumns,

  // Fallback
  IconBox,
};

/**
 * Get an icon component by name, with fallback to IconBox.
 *
 * @param iconName - The icon name from SubgraphMetadata (e.g., "IconHeartbeat")
 * @returns The corresponding Tabler icon component
 */
export function getIcon(iconName: string): Icon {
  return ICON_MAP[iconName] ?? IconBox;
}

export default ICON_MAP;
